import { DirectUploadError } from '../lib/direct-upload';

const MB = 1024 * 1024;

// The API's per-file caps (spec decision 15); checked here so a refused file costs no request.
export const GALLERY_PHOTO_MAX_BYTES = 50 * MB;
export const GALLERY_VIDEO_MAX_BYTES = 1024 * MB;
export const GALLERY_UPLOAD_CONCURRENCY = 4;
export const GALLERY_UPLOAD_ACCEPT = 'image/*,video/mp4,video/quicktime,.mov,.heic,.heif';

export const GALLERY_UPLOAD_ERROR_CODES = [
  'FILE_TYPE',
  'PHOTO_TOO_LARGE',
  'VIDEO_TOO_LARGE',
  'GALLERY_VIDEO_TOO_LONG',
  'GALLERY_VIDEO_NOT_H264',
  'GALLERY_VIDEO_UNREADABLE',
  'STORAGE_LIMIT_EXCEEDED',
  'FAILED',
] as const;

export type GalleryUploadErrorCode = (typeof GALLERY_UPLOAD_ERROR_CODES)[number];

export type GalleryUploadStatus = 'queued' | 'uploading' | 'done' | 'failed';

export interface GalleryUploadEntry {
  id: string;
  fileName: string;
  size: number;
  status: GalleryUploadStatus;
  // Share sent to storage, 0..1.
  progress: number;
  error: GalleryUploadErrorCode | null;
}

export interface GalleryUploadSummary {
  total: number;
  done: number;
  failed: number;
  // Queued or uploading.
  active: number;
  // Bytes sent over bytes to send, failed files left out.
  progress: number;
}

const API_ERROR_CODES = new Set<GalleryUploadErrorCode>([
  'GALLERY_VIDEO_TOO_LONG',
  'GALLERY_VIDEO_NOT_H264',
  'GALLERY_VIDEO_UNREADABLE',
  'STORAGE_LIMIT_EXCEEDED',
]);

const VIDEO_TYPES = new Set(['video/mp4', 'video/quicktime']);
const VIDEO_EXTENSIONS = new Set(['mp4', 'mov']);
const PHOTO_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'webp',
  'heic',
  'heif',
  'avif',
  'gif',
  'tif',
  'tiff',
]);

function kindOf(file: Pick<File, 'name' | 'type'>): 'IMAGE' | 'VIDEO' | null {
  const type = file.type.trim().toLowerCase();
  if (type.startsWith('image/')) {
    return 'IMAGE';
  }
  if (type) {
    return VIDEO_TYPES.has(type) ? 'VIDEO' : null;
  }

  // Some browsers give no type for MOV or HEIC files.
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (VIDEO_EXTENSIONS.has(extension)) {
    return 'VIDEO';
  }
  return PHOTO_EXTENSIONS.has(extension) ? 'IMAGE' : null;
}

export function validateGalleryFile(
  file: Pick<File, 'name' | 'type' | 'size'>
): GalleryUploadErrorCode | null {
  const kind = kindOf(file);
  if (!kind) {
    return 'FILE_TYPE';
  }
  if (kind === 'IMAGE' && file.size > GALLERY_PHOTO_MAX_BYTES) {
    return 'PHOTO_TOO_LARGE';
  }
  if (kind === 'VIDEO' && file.size > GALLERY_VIDEO_MAX_BYTES) {
    return 'VIDEO_TOO_LARGE';
  }
  return null;
}

export function uploadErrorCodeOf(error: unknown): GalleryUploadErrorCode {
  const code = error instanceof DirectUploadError ? error.code : undefined;
  return code && API_ERROR_CODES.has(code as GalleryUploadErrorCode)
    ? (code as GalleryUploadErrorCode)
    : 'FAILED';
}

export interface RunGalleryUploadsOptions {
  concurrency?: number;
  upload: (file: File, onProgress: (share: number) => void) => Promise<unknown>;
  // Called with a fresh array on every change.
  onChange: (entries: GalleryUploadEntry[]) => void;
  onUploaded?: (entry: GalleryUploadEntry) => void;
}

let nextEntryId = 0;

// Uploads files with at most `concurrency` at once, in the order given; a failed file never
// stops the others. Resolves with the final state of every file.
export async function runGalleryUploads(
  files: File[],
  {
    concurrency = GALLERY_UPLOAD_CONCURRENCY,
    upload,
    onChange,
    onUploaded,
  }: RunGalleryUploadsOptions
): Promise<GalleryUploadEntry[]> {
  let entries: GalleryUploadEntry[] = files.map((file) => {
    const error = validateGalleryFile(file);
    nextEntryId += 1;
    return {
      id: `upload-${nextEntryId}`,
      fileName: file.name,
      size: file.size,
      status: error ? 'failed' : 'queued',
      progress: 0,
      error,
    };
  });
  onChange(entries);

  const patch = (index: number, change: Partial<GalleryUploadEntry>) => {
    entries = entries.map((entry, at) => (at === index ? { ...entry, ...change } : entry));
    onChange(entries);
    return entries[index];
  };

  const queue = entries
    .map((entry, index) => index)
    .filter((index) => entries[index].status === 'queued');

  const worker = async () => {
    for (let index = queue.shift(); index !== undefined; index = queue.shift()) {
      const at = index;
      patch(at, { status: 'uploading' });
      try {
        await upload(files[at], (share) => {
          if (entries[at].status === 'uploading') {
            patch(at, { progress: Math.max(0, Math.min(1, share)) });
          }
        });
        const finished = patch(at, { status: 'done', progress: 1 });
        onUploaded?.(finished);
      } catch (error) {
        patch(at, { status: 'failed', error: uploadErrorCodeOf(error) });
      }
    }
  };

  const workers = Math.max(1, Math.min(concurrency, queue.length));
  await Promise.all(Array.from({ length: workers }, worker));

  return entries;
}

export function summarizeUploads(entries: GalleryUploadEntry[]): GalleryUploadSummary {
  let sent = 0;
  let toSend = 0;
  const summary = { total: entries.length, done: 0, failed: 0, active: 0, progress: 0 };

  for (const entry of entries) {
    if (entry.status === 'failed') {
      summary.failed += 1;
      continue;
    }
    if (entry.status === 'done') {
      summary.done += 1;
    } else {
      summary.active += 1;
    }
    sent += entry.size * entry.progress;
    toSend += entry.size;
  }

  summary.progress = toSend > 0 ? sent / toSend : 0;
  return summary;
}
